/**
 * Utility functions for creating branded business card images
 */

import { BusinessCardData } from '@/types';

export interface BrandedCardOptions {
  qrCodeUrl: string;
  formData: BusinessCardData;
  primaryColor?: string;
  secondaryColor?: string;
  azureProfileId?: string;
  getAccessToken?: () => Promise<string>;
}

/**
 * Creates a branded business card image with QR code and user details
 * @param options - Configuration options for the branded card
 * @param format - Output format ('png' or 'jpeg')
 * @returns Promise resolving to data URL of the generated image
 */
export const createBrandedCardImage = async (
  options: BrandedCardOptions,
  format: 'png' | 'jpeg' = 'png'
): Promise<string> => {
  const {
    qrCodeUrl,
    formData,
    primaryColor = '#1E3A8A',
    secondaryColor = '#FFFFFF',
    azureProfileId,
    getAccessToken
  } = options;

  return new Promise(async (resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }

      // Card dimensions (business card style)
      const cardWidth = 1200;
      const cardHeight = 700;
      canvas.width = cardWidth;
      canvas.height = cardHeight;

      // Background - white or gradient
      ctx.fillStyle = secondaryColor;
      ctx.fillRect(0, 0, cardWidth, cardHeight);

      // Add subtle border
      ctx.strokeStyle = '#E5E7EB';
      ctx.lineWidth = 2;
      ctx.strokeRect(10, 10, cardWidth - 20, cardHeight - 20);

      // Load QR code
      const qrImg = document.createElement('img');
      qrImg.crossOrigin = 'anonymous';
      qrImg.src = qrCodeUrl;
      
      await new Promise((resolveImg) => {
        qrImg.onload = resolveImg;
      });

      // Draw QR code on right side
      const qrSize = 400;
      const qrX = cardWidth - qrSize - 60;
      const qrY = (cardHeight - qrSize) / 2;
      ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

      // Left side - User information
      const leftMargin = 60;
      let yPosition = 100;

      // Try to load and draw profile picture
      let profilePictureDrawn = false;
      const profilePicSize = 120;
      const profilePicX = leftMargin;
      const profilePicY = yPosition;

      // Check if Azure profile has a photo URL
      if (azureProfileId && getAccessToken) {
        try {
          // Try to load profile picture from Microsoft Graph
          const photoUrl = `https://graph.microsoft.com/v1.0/users/${azureProfileId}/photo/$value`;
          const token = await getAccessToken();
          
          const profileImg = document.createElement('img');
          profileImg.crossOrigin = 'anonymous';
          
          // Create a promise to load the profile picture
          const loadProfilePic = new Promise<boolean>((resolveImg) => {
            profileImg.onload = () => {
              // Draw circular profile picture
              ctx.save();
              ctx.beginPath();
              ctx.arc(profilePicX + profilePicSize / 2, profilePicY + profilePicSize / 2, profilePicSize / 2, 0, Math.PI * 2);
              ctx.closePath();
              ctx.clip();
              
              // Draw the image
              ctx.drawImage(profileImg, profilePicX, profilePicY, profilePicSize, profilePicSize);
              ctx.restore();
              
              // Draw border around profile picture
              ctx.strokeStyle = primaryColor;
              ctx.lineWidth = 4;
              ctx.beginPath();
              ctx.arc(profilePicX + profilePicSize / 2, profilePicY + profilePicSize / 2, profilePicSize / 2, 0, Math.PI * 2);
              ctx.stroke();
              
              resolveImg(true);
            };
            
            profileImg.onerror = () => resolveImg(false);
            
            // Load with authentication
            fetch(photoUrl, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            })
              .then(response => response.blob())
              .then(blob => {
                profileImg.src = URL.createObjectURL(blob);
              })
              .catch(() => resolveImg(false));
          });
          
          profilePictureDrawn = await loadProfilePic;
        } catch (error) {
          profilePictureDrawn = false;
        }
      }

      // If no profile picture, draw initials placeholder
      if (!profilePictureDrawn) {
        // Draw circular background
        ctx.fillStyle = primaryColor;
        ctx.beginPath();
        ctx.arc(profilePicX + profilePicSize / 2, profilePicY + profilePicSize / 2, profilePicSize / 2, 0, Math.PI * 2);
        ctx.fill();
        
        // Draw initials
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 48px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const initials = formData.displayName
          .split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase()
          .substring(0, 2);
        ctx.fillText(initials, profilePicX + profilePicSize / 2, profilePicY + profilePicSize / 2);
        
        // Reset text alignment
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';
      }

      // Adjust left margin and y position for text after profile picture
      const textLeftMargin = leftMargin + profilePicSize + 30;
      const maxTextWidth = qrX - textLeftMargin - 80; // Leave 80px gap before QR code
      yPosition = profilePicY + 30;

      // Company logo or colored header bar
      ctx.fillStyle = primaryColor;
      ctx.fillRect(0, 0, cardWidth, 8);

      // Helper function to wrap text
      const wrapText = (text: string, x: number, y: number, maxWidth: number, lineHeight: number): number => {
        const words = text.split(' ');
        let line = '';
        let currentY = y;
        
        for (let n = 0; n < words.length; n++) {
          const testLine = line + words[n] + ' ';
          const metrics = ctx.measureText(testLine);
          const testWidth = metrics.width;
          
          if (testWidth > maxWidth && n > 0) {
            ctx.fillText(line.trim(), x, currentY);
            line = words[n] + ' ';
            currentY += lineHeight;
          } else {
            line = testLine;
          }
        }
        ctx.fillText(line.trim(), x, currentY);
        return currentY + lineHeight;
      };

      // Name (Large, bold) - with wrapping
      ctx.fillStyle = '#1F2937';
      ctx.font = 'bold 48px Arial, sans-serif';
      yPosition = wrapText(formData.displayName, textLeftMargin, yPosition, maxTextWidth, 55);

      // Job Title - with wrapping
      if (formData.jobTitle) {
        ctx.fillStyle = '#4B5563';
        ctx.font = '32px Arial, sans-serif';
        yPosition = wrapText(formData.jobTitle, textLeftMargin, yPosition, maxTextWidth, 40);
      }

      // Company Name - with wrapping
      if (formData.companyName) {
        ctx.fillStyle = '#6B7280';
        ctx.font = 'bold 28px Arial, sans-serif';
        yPosition = wrapText(formData.companyName, textLeftMargin, yPosition, maxTextWidth, 35);
        yPosition += 5;
      }

      // Move to next section for contact info
      yPosition = Math.max(yPosition, profilePicY + profilePicSize + 30);

      // Add separator line
      yPosition += 20;
      ctx.strokeStyle = '#E5E7EB';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(leftMargin, yPosition);
      ctx.lineTo(qrX - 60, yPosition);
      ctx.stroke();
      yPosition += 40;

      // Contact Information - with wrapping
      ctx.fillStyle = '#374151';
      ctx.font = '24px Arial, sans-serif';

      // Email
      if (formData.mail) {
        const emailText = '📧 ' + formData.mail;
        yPosition = wrapText(emailText, leftMargin, yPosition, qrX - leftMargin - 80, 35);
      }

      // Phone
      if (formData.mobilePhone) {
        const phoneText = '📱 ' + formData.mobilePhone;
        yPosition = wrapText(phoneText, leftMargin, yPosition, qrX - leftMargin - 80, 35);
      }

      // Department
      if (formData.department) {
        const deptText = '🏢 ' + formData.department;
        yPosition = wrapText(deptText, leftMargin, yPosition, qrX - leftMargin - 80, 35);
      }

      // Address
      if (formData.address) {
        const parts = [formData.address.street, formData.address.city, formData.address.state, formData.address.postalCode, formData.address.country].filter(Boolean);
        if (parts.length > 0) {
          const addrText = '📍 ' + parts.join(', ');
          yPosition = wrapText(addrText, leftMargin, yPosition, qrX - leftMargin - 80, 35);
        }
      }

      // Website
      if (formData.website) {
        const webText = '🌐 ' + formData.website;
        yPosition = wrapText(webText, leftMargin, yPosition, qrX - leftMargin - 80, 35);
      }

      // LinkedIn / Social Profiles
      if (formData.socialProfiles && formData.socialProfiles.length > 0) {
        for (const profile of formData.socialProfiles) {
          if (profile.url) {
            const icon = profile.platform.toLowerCase().includes('linkedin') ? '💼' : '🔗';
            const socialText = `${icon} ${profile.url}`;
            yPosition = wrapText(socialText, leftMargin, yPosition, qrX - leftMargin - 80, 35);
          }
        }
      }

      // Footer text
      ctx.fillStyle = '#9CA3AF';
      ctx.font = 'italic 20px Arial, sans-serif';
      ctx.fillText('Scan QR code to save contact', leftMargin, cardHeight - 40);

      // Convert to data URL
      if (format === 'jpeg') {
        resolve(canvas.toDataURL('image/jpeg', 0.95));
      } else {
        resolve(canvas.toDataURL('image/png'));
      }
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Generate a filename for the business card export
 * @param displayName - Name to use in the filename
 * @param extension - File extension
 * @returns Sanitized filename
 */
export const generateCardFileName = (displayName: string, extension: string): string => {
  return `business-card-${displayName.replace(/\s+/g, '-').toLowerCase()}.${extension}`;
};
